import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit3, Trash2, Plus, Loader2, Image as ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from "../../../../api/axios";

const ShowSousCategory = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null); // Tracks which category is open

    const fetchCategories = async () => {
        try {
            const res = await api.get("/categories");
            if (res.data.success) {
                setCategories(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching categories:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const handleDelete = async (id, isSub = false) => {
        if (!window.confirm(`Voulez-vous supprimer cette ${isSub ? 'sous-' : ''}catégorie ?`)) return;
        try {
            const res = await api.delete(`/categories/${id}`);
            if (res.data.success) {
                // Refresh list to show updated structure
                fetchCategories();
            }
        } catch (err) {
            alert("Erreur lors de la suppression.");
        }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl mx-auto px-4 pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h2 className="text-2xl font-black uppercase tracking-tight text-gray-900">Structure des Catégories</h2>
                    <p className="text-sm font-bold text-[#8A8A89] mt-1">ELECTRO TITAN Inventory</p>
                </div>
                <Link to="/admin/dashboard/add-category" className="flex items-center justify-center gap-2 bg-[#ffaa00] text-white px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-[0.15em] shadow-lg hover:bg-black transition-all">
                    <Plus size={18} /> Nouvelle Catégorie
                </Link>
            </div>

            {/* Table Container */}
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/30 border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-separate border-spacing-0">
                            <thead>
                                <tr className="bg-gray-50/50">
                                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Nom de la Catégorie</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {categories.map((cat) => (
                                    <React.Fragment key={cat.id}>
                                        {/* Parent Row */}
                                        <tr className="hover:bg-gray-50/30 transition-colors group">

                                            <td className="px-8 py-5">
                                                <div className="flex flex-col">
                                                    <span className="font-black text-gray-900">{cat.name}</span>
                                                    <span className="text-[10px] font-bold text-[#8A8A89] uppercase tracking-tighter">
                                                        {cat.children?.length || 0} Sous-catégories
                                                    </span>
                                                </div>
                                            </td>
                                            
                                            <td className="py-5">
                                                <button
                                                    onClick={() => toggleExpand(cat.id)}
                                                    className={`p-2 rounded-lg transition-all ${expandedId === cat.id ? 'bg-[#ffaa00] text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                                                >
                                                    {expandedId === cat.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                </button>
                                            </td>

                                        </tr>

                                        {/* Expandable Sub-table */}
                                        <AnimatePresence>
                                            {expandedId === cat.id && (
                                                <tr>
                                                    <td colSpan="4" className="px-8 pb-6 bg-gray-50/30">
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: "auto", opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            className="overflow-hidden bg-white border border-gray-100 rounded-3xl shadow-inner mx-4"
                                                        >
                                                            <table className="w-full">
                                                                <thead className="border-b border-gray-50">
                                                                    <tr>
                                                                        <th className="px-6 py-4 text-[9px] font-black uppercase text-gray-400 tracking-[0.2em]">Nom Sous-Catégorie</th>
                                                                        <th className="px-6 py-4 text-[9px] font-black uppercase text-gray-400 tracking-[0.2em] text-right">Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-50">
                                                                    {cat.children && cat.children.length > 0 ? (
                                                                        cat.children.map((sub) => (
                                                                            <tr key={sub.id} className="hover:bg-gray-50/50">
                                                                                <td className="px-6 py-4">
                                                                                    <div className="flex items-center gap-3">
                                                                                        <div className="w-1.5 h-1.5 rounded-full bg-[#ffaa00]"></div>
                                                                                        <span className="text-sm font-bold text-gray-700">{sub.name}</span>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-6 py-4 text-right">
                                                                                    <div className="flex justify-end gap-2">
                                                                                        <Link to={`../edit-sous-category/${sub.id}`} className="p-2 text-gray-400 hover:text-black transition-colors">
                                                                                            <Edit3 size={14} />
                                                                                        </Link>
                                                                                        <button onClick={() => handleDelete(sub.id, true)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                                                                                            <Trash2 size={14} />
                                                                                        </button>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    ) : (
                                                                        <tr>
                                                                            <td colSpan="2" className="px-6 py-8 text-center text-xs font-bold text-gray-400 italic">
                                                                                Aucune sous-catégorie disponible.
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </motion.div>
                                                    </td>
                                                </tr>
                                            )}
                                        </AnimatePresence>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default ShowSousCategory;